import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useCartProductsCache } from "~/hooks/use-cart";
import Editorials from "~/types/editorials";

import styles from "../../styles/pages/cart.module.scss";
import RichText from "../common/rich-text";
import SkeletonCartKissesInfo from "../common/skeletons/skeleton-cart-kisses-info";
import SkeletonCartRightColumn from "../common/skeletons/skeleton-cart-right-column";
import CustomerCare from "../customer-care/customer-care";
import CartAccordions from "./cart-accordions";
import CartCouponSelector from "./cart-coupon-selector";
import CartKissesInfo from "./cart-kisses-info";
import CartPickProductSection from "./cart-pick-product-section";
import CartProducts from "./cart-products";
import CartSavedForLater from "./cart-saved-for-later";
import CartTotals from "./cart-totals";

export default function CartLayout({ page }: { page: Editorials.CartPage }) {
  const { isAuthenticated, isLoadingUser } = useAuthContext();
  const { isPendingCart } = useCartProductsCache();
  const breakpoint = useBreakpoint();
  const refWrapperCartTotalsOnMobile = useRef<HTMLDivElement>(null);
  const [isSticked, setIsSticked] = useState(false);
  const showSkeleton = useMemo(() => isPendingCart || isLoadingUser, [isPendingCart, isLoadingUser]);
  const withImage = useMemo(() => {
    return (
      !!(isAuthenticated && page?.loggedLoyaltyProgramMedia) || !!(!isAuthenticated && page?.guestLoyaltyProgramMedia)
    );
  }, [isAuthenticated, page]);

  useEffect(() => {
    if (breakpoint !== "mobile" || !refWrapperCartTotalsOnMobile.current) {
      setIsSticked(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([e]) => setIsSticked(e.intersectionRatio < 1 && e.intersectionRect.top > 0),
      {
        threshold: [1],
        rootMargin: "0px 0px -9px 0px", //to detect the element in sticky mode
      }
    );

    observer.observe(refWrapperCartTotalsOnMobile.current);

    return () => {
      observer.disconnect();
    };
  }, [breakpoint]);

  const rightColumnCommon = (
    <>
      <CartTotals refWrapperCartTotalsOnMobile={refWrapperCartTotalsOnMobile} isSticked={isSticked} />
      <CustomerCare />
      <CartAccordions page={page} />
    </>
  );

  return (
    <>
      <div className={styles.column1}>
        {breakpoint === "mobile" && (
          <>
            {page?.showHighlightedMessage && page?.highlightedMessage && (
              <div className={styles.customDutiesContainer}>
                <p className={styles.customDutiesText}>
                  <RichText text={page.highlightedMessage} />
                </p>
              </div>
            )}
            {showSkeleton ? (
              <SkeletonCartKissesInfo page={page} />
            ) : (
              <CartKissesInfo page={page} withImage={withImage} />
            )}
            <CartCouponSelector />
          </>
        )}
        <CartProducts emptyLinks={page?.emptyLinks} trackingScope="cart" />
        <CartPickProductSection />
        {isAuthenticated && <CartSavedForLater />}
      </div>
      <div className={styles.column2}>
        {breakpoint === "desktop" &&
          (showSkeleton ? (
            <SkeletonCartRightColumn page={page} />
          ) : (
            <>
              <CartKissesInfo page={page} withImage={withImage} />
              {page?.showHighlightedMessage && page?.highlightedMessage && (
                <div className={styles.customDutiesContainer}>
                  <p className={styles.customDutiesText}>
                    <RichText text={page.highlightedMessage} />
                  </p>
                </div>
              )}
              <CartCouponSelector />
              {rightColumnCommon}
            </>
          ))}
      </div>
      {breakpoint === "mobile" && (
        <div
          className={classNames(styles.cartTotalsSticky, isSticked && styles.triggerSticky)}
          ref={refWrapperCartTotalsOnMobile}
        >
          {rightColumnCommon}
        </div>
      )}
    </>
  );
}
